import * as React from 'react';

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    Module,
    Node,
    ITelemetryContent,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';

import { ISignInItem, ISignInLoading, ISignInLocalAccount, ISignInViewProps } from '@msdyn365-commerce-modules/account-management';

import { ISignInProps } from '../definition-extensions/sign-in.ext.props.autogenerated';

const LocalAccount: React.FC<ISignInLocalAccount> = ({ localAccount, items, forgetPassword, disclaimer, error, signInButton }) => {
    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignInItem) => {
                    return (
                        <Node key={item.key} {...item.wrapper}>
                            {item.label}
                            {item.error}
                            {item.input}
                        </Node>
                    );
                })}
            {forgetPassword}
            {disclaimer}
            {error}
            {signInButton}
        </Node>
    );
};

const SignInLoading: React.FC<ISignInLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignInView: React.FC<ISignInViewProps & ISignInProps<{}>> = props => {
    const {
        signIn,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        signInSection,
        signUpSection,
        signInSectionHeading,
        signInLocalAccount,
        signUpSectionHeading,
        signUpDescription,
        signUpLink
    } = props;
    const canonicalDomain = props.signIn.moduleProps.context.request.canonicalDomain;
    const sitePath =
        props.signIn.moduleProps.context.request.sitePath && props.signIn.moduleProps.context.request.sitePath !== null
            ? props.signIn.moduleProps.context.request.sitePath
            : '';
    const requestUrl: string = `https://${canonicalDomain}${sitePath}`;

    const telemetryContent: ITelemetryContent = getTelemetryObject(
        props.context.request.telemetryPageName!,
        props.friendlyName,
        props.telemetry
    );
    const payLoad = getPayloadObject('click', telemetryContent!, props.resources.signUpButtonText);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    const showContinueAsGuest = props.config.showContinueAsGuestButton;
    const showVclubColumn = props.config.showVclubColumn;

    React.useEffect(() => {
        if (!viewState.isShowLoading) {
            const registerButtonLink = document.getElementById('createAccount');
            const vclubButtonLink = document.getElementById('vclub-signin');

            if (registerButtonLink && vclubButtonLink) {
                // @ts-ignore
                vclubButtonLink.href = registerButtonLink.href;
            }
        }
    }, [viewState.isShowLoading]);

    return (
        <Module {...signIn}>
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignInLoading {...loading} />}
            <Node {...aadConainer}>
                <Node {...signInSection} className={`col-12 col-lg-4 ${signInSection.className}`}>
                    {signInSectionHeading}
                    {LocalAccount(signInLocalAccount)}
                </Node>
                {showVclubColumn && (
                    <Node className={`col-12 col-lg-4 ms-sign-in__vclub-section`}>
                        <h2 className='ms-sign-in__sign-up-heading'>{props.resources.signinVclubTitle}</h2>
                        <p className='ms-sign-in__sign-up-description'>{props.resources.signinVclubText}</p>
                        <div>
                            <a
                                id='vclub-signin'
                                className='ms-sign-in__sign-up-link msc-btn'
                                href='#'
                                aria-label={props.resources.signUpButtonText}
                                {...attributes}
                            >
                                {props.resources.signUpButtonText}
                            </a>
                        </div>

                        {showContinueAsGuest && (
                            <div>
                                <a className={'msc-cart__btn-guestcheckout'} href={`${requestUrl}/checkout`}>
                                    {props.resources.continueAsGuest ?? 'Continue as guest'}
                                </a>
                            </div>
                        )}
                    </Node>
                )}
                <Node {...signUpSection} className={`col-12 col-lg-4 ${signUpSection.className}`}>
                    {signUpSectionHeading}
                    {signUpDescription}
                    <div>{signUpLink}</div>

                    {showContinueAsGuest && (
                        <div>
                            <a className={'msc-cart__btn-guestcheckout'} href={`${requestUrl}/checkout`}>
                                {props.resources.continueAsGuest ?? 'Continue as guest'}
                            </a>
                        </div>
                    )}
                </Node>
            </Node>
        </Module>
    );
};

export default SignInView;
